import {
  createContext,
  useContext,
  useMemo,
  useState,
  type ReactNode,
} from 'react'
import type {
  StateSetter,
  StrategicAsset,
  StrategicAssetCoverage,
} from '../types'

// ProofContext owns the strategic-proof state previously held in App.jsx
// as two useState entries: `strategicAssets` (the full array of indexed
// briefs/reports/diagrams) and `strategicAssetCoverage` (the rollup
// payload returned alongside).
//
// Wave 7 Phase D — closes the App.jsx bootstrap-state migration. No
// reducer here: the two slots are independent and the legacy code
// always set them with the same `setStrategicAssets` / `setCoverage`
// pair, no derived logic.
// Wave 15 — promoted to TypeScript.

export interface ProofContextValue {
  assets: StrategicAsset[]
  coverage: StrategicAssetCoverage | null
  setAssets: StateSetter<StrategicAsset[]>
  setCoverage: StateSetter<StrategicAssetCoverage | null>
}

const DEFAULT_STATE = Object.freeze({
  assets: [] as StrategicAsset[],
  coverage: null as StrategicAssetCoverage | null,
})

const noop = (): void => {}

const ProofContext = createContext<ProofContextValue>({
  assets: [],
  coverage: null,
  setAssets: noop,
  setCoverage: noop,
})

export interface ProofProviderProps {
  children?: ReactNode
  initialState?: {
    assets?: StrategicAsset[]
    coverage?: StrategicAssetCoverage | null
  }
}

export function ProofProvider({
  children,
  initialState = {},
}: ProofProviderProps) {
  const [assets, setAssets] = useState<StrategicAsset[]>(
    initialState.assets || []
  )
  const [coverage, setCoverage] = useState<StrategicAssetCoverage | null>(
    initialState.coverage === undefined ? null : initialState.coverage
  )

  const value = useMemo<ProofContextValue>(
    () => ({
      assets,
      coverage,
      setAssets,
      setCoverage,
    }),
    [assets, coverage]
  )

  return <ProofContext.Provider value={value}>{children}</ProofContext.Provider>
}

export function useProof(): ProofContextValue {
  return useContext(ProofContext)
}

export { DEFAULT_STATE, ProofContext }
